import { useMemo } from "react";
import { dayIndex, dayLengthSeries, formatDuration } from "@/lib/solar";

const W = 900;
const H = 260;
const PAD = { top: 20, right: 20, bottom: 30, left: 44 };
const MUTED = "#8a6f52";

const py = (h: number) => PAD.top + ((24 - h) / 24) * (H - PAD.top - PAD.bottom);
const makeX = (n: number) => (i: number) => PAD.left + (i / (n - 1)) * (W - PAD.left - PAD.right);
const fmtDay = (year: number, i: number) =>
  new Date(year, 0, 1 + i).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function Gridlines() {
  return (
    <>
      {[0, 6, 12, 18, 24].map((h) => (
        <g key={h}>
          <line x1={PAD.left} x2={W - PAD.right} y1={py(h)} y2={py(h)} stroke="#e6dac4" strokeDasharray={h === 12 ? "0" : "2 4"} />
          <text x={PAD.left - 8} y={py(h) + 3} textAnchor="end" fontSize="10" fontFamily="Inter, sans-serif" fill={MUTED}>
            {h}h
          </text>
        </g>
      ))}
    </>
  );
}

function MonthLabels({ year, x }: { year: number; x: (i: number) => number }) {
  return (
    <>
      {Array.from({ length: 12 }, (_, m) => {
        const first = new Date(year, m, 1);
        return (
          <text key={m} x={x(dayIndex(first)) + 4} y={H - 10} fontSize="10" fontFamily="'Fraunces Variable', serif" fontStyle="italic" fill={MUTED}>
            {first.toLocaleDateString(undefined, { month: "short" })}
          </text>
        );
      })}
    </>
  );
}

function TodayMarker({ cx, hours, flip }: { cx: number; hours: number; flip: boolean }) {
  const cy = py(hours);
  return (
    <g>
      <line x1={cx} x2={cx} y1={PAD.top} y2={py(0)} stroke="#2b1f14" strokeOpacity="0.4" strokeDasharray="2 3" />
      <circle cx={cx} cy={cy} r="10" fill="#c0501b" opacity="0.15" />
      <circle cx={cx} cy={cy} r="5" fill="#c0501b" stroke="#f6efe3" strokeWidth="1.5" />
      <text x={cx + (flip ? -12 : 12)} y={cy - 10} textAnchor={flip ? "end" : "start"} fontSize="12" fontFamily="'Fraunces Variable', serif" fontStyle="italic" fill="#2b1f14">
        today · {formatDuration(hours * 60)}
      </text>
    </g>
  );
}

// Index where a run of `value` begins, treating the year as circular (polar seasons wrap past Dec 31).
function runStart(hours: number[], value: number): number {
  const n = hours.length;
  const start = hours.findIndex((h, i) => h === value && hours[(i - 1 + n) % n] !== value);
  return start === -1 ? hours.indexOf(value) : start;
}

// Summary text for the longest/shortest day, handling flat (equator) and polar cases.
function describeExtremes(hours: number[], year: number) {
  const max = Math.max(...hours);
  const min = Math.min(...hours);
  const longestDay = fmtDay(year, runStart(hours, max));
  const shortestDay = fmtDay(year, runStart(hours, min));
  const flat = max - min < 0.1;
  const same = "About the same all year";
  return {
    longest: flat ? same : max >= 23.99 ? `Midnight sun from ${longestDay}` : `${longestDay} · ${formatDuration(max * 60)}`,
    shortest: flat ? same : min <= 0.01 ? `Polar night from ${shortestDay}` : `${shortestDay} · ${formatDuration(min * 60)}`,
    swing: formatDuration((max - min) * 60),
  };
}

function SummaryItem({ label, value, testid }: { label: string; value: string; testid?: string }) {
  return (
    <div>
      <dt className="uppercase tracking-[0.3em]">{label}</dt>
      <dd data-testid={testid} className="mt-1 font-serif text-lg text-[#2b1f14]">
        {value}
      </dd>
    </div>
  );
}

export default function DayLengthChart({ now, latitude }: { now: Date; latitude: number }) {
  const year = now.getFullYear();
  const hours = useMemo(() => dayLengthSeries(year, latitude), [year, latitude]);
  const summary = useMemo(() => describeExtremes(hours, year), [hours, year]);
  const x = makeX(hours.length);
  const line = hours.map((h, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${py(h).toFixed(1)}`).join(" ");
  const todayIdx = Math.min(dayIndex(now), hours.length - 1);

  return (
    <div data-testid="solar-day-length-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="Hours of daylight across the year">
        <Gridlines />
        <MonthLabels year={year} x={x} />
        <path d={`${line} L${x(hours.length - 1)},${py(0)} L${x(0)},${py(0)} Z`} fill="#c0501b" fillOpacity="0.08" />
        <path d={line} fill="none" stroke="#c0501b" strokeWidth="1.6" />
        <TodayMarker cx={x(todayIdx)} hours={hours[todayIdx]} flip={todayIdx > hours.length * 0.8} />
      </svg>
      <dl className="mt-4 grid grid-cols-2 gap-6 font-sans text-xs text-[#8a6f52] md:grid-cols-3">
        <SummaryItem label="Longest day" value={summary.longest} testid="solar-longest-day" />
        <SummaryItem label="Shortest day" value={summary.shortest} testid="solar-shortest-day" />
        <SummaryItem label="Swing" value={summary.swing} />
      </dl>
    </div>
  );
}
